import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bug, Check, Download, Monitor, MoonStar, MoreHorizontal, Sun } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import { ExportHistoryPanel } from '@/components/export-history/ExportHistoryPanel'
import { useTheme } from '@/components/providers/theme-provider'
import { useLocale } from '@/i18n/useLocale'
import { LOCALE_ENDONYMS, SUPPORTED_LOCALES, type AppLocale } from '@/i18n/locales'
import { openReportIssue } from '@/config/reportIssue'
import { cn } from '@/lib/utils'

interface Props {
  /** 上报 App Bug 时带上的当前用户（明文，仅供 QA 联系）。 */
  userId: string | null | undefined
  userName: string | null | undefined
}

const THEMES = [
  { value: 'light' as const, Icon: Sun },
  { value: 'dark' as const, Icon: MoonStar },
  { value: 'system' as const, Icon: Monitor },
]

/**
 * 顶栏 ⋯ 溢出菜单（R-044）。
 *
 * 顶栏只留高频入口（Create / AI / Watchlist / Reminder / Notification），低频的
 * 全部收进这里：报告 App Bug、My downloads、语言、Screen mode。语言和主题**直接**
 * 在菜单里选（不再各占一个顶栏图标、也不做二级弹层）——用户给的参照样式就是
 * 一屏能看完、一次点击切换。
 *
 * My downloads 是唯一的例外：它的内容是个带搜索的长列表，塞进菜单会挤爆，所以
 * 走 Popover，并用 PopoverAnchor 挂在 ⋯ 按钮上，视觉上仍是「从这个按钮展开的」。
 */
export function TopBarMoreMenu({ userId, userName }: Props) {
  const { t } = useTranslation('nav')
  const { t: tc } = useTranslation('common')
  const { locale, setLocale } = useLocale()
  const { theme, setTheme } = useTheme()
  const [menuOpen, setMenuOpen] = useState(false)
  const [downloadsOpen, setDownloadsOpen] = useState(false)

  const triggerRef = useRef<HTMLButtonElement>(null)

  // 从菜单里开下载面板要躲开菜单关闭时的收尾事件（实测：不躲就是"点了没反应"——
  // 面板 mount 完当场收到 onOpenChange(false)）：
  //   1. 选中菜单项那一下的 pointerup/click 继续派发，落在刚挂上的 Popover
  //      dismissable layer 外面，被当成 interact-outside；
  //   2. 菜单关闭时 Radix 把焦点还给 ⋯ 触发器，同样算 focus-outside。
  // 对策：菜单先关 → 下一帧再开面板 → 面板挂上后忽略**回声**事件。
  /** 只消费一次：跳过菜单关闭时的自动回焦（否则焦点被抢回 ⋯，面板当场被判 focus-outside）。 */
  const skipMenuAutoFocusRef = useRef(false)
  /** 菜单收尾期：从"选中 My downloads"到面板挂稳的这几帧，focus 类事件一律算回声。 */
  const menuTeardownRef = useRef(false)
  /** 面板打开的时刻，取 performance.now()——与 DOM 事件的 timeStamp 同一时钟。 */
  const downloadsOpenedAtRef = useRef(0)

  const openDownloads = () => {
    skipMenuAutoFocusRef.current = true
    menuTeardownRef.current = true
    setMenuOpen(false)
    requestAnimationFrame(() => {
      downloadsOpenedAtRef.current = performance.now()
      setDownloadsOpen(true)
      // 面板挂上 + 菜单的回焦都走完，再退出收尾期。用帧边界而不是毫秒数：
      // 这里等的是"渲染与焦点归位完成"，不是"过了多久"。
      requestAnimationFrame(() => requestAnimationFrame(() => { menuTeardownRef.current = false }))
    })
  }

  /**
   * 指针类回声：用**因果**判定，不用时间窗。事件 timeStamp 与 performance.now() 同源
   * （都以 timeOrigin 为零点），`timeStamp <= 面板打开时刻` 意味着这次交互**发生在面板
   * 存在之前** —— 不可能是用户针对面板做的"点外面"，只能是选中菜单项那一下的余波。
   *
   * 早先这里是 300ms 时间窗，代价是用户在开面板后 300ms 内真的点外面那一下会被吞掉
   * （得再点一次才关）。改成因果判据后，真实的外部点击一次即关，误吞为 0。
   */
  const ignoreEchoInteract = (e: Event & { detail?: { originalEvent?: Event } }) => {
    const ts = e.detail?.originalEvent?.timeStamp
    if (typeof ts === 'number' && ts > 0 && ts <= downloadsOpenedAtRef.current) e.preventDefault()
  }

  /**
   * 焦点类回声：菜单卸载时把焦点还回 ⋯ 这件事**发生在面板挂上之后**，时间上晚于打开，
   * 因果判据盖不住（试过：面板当场被关）。所以焦点这条按"来源"判 ——
   * 收尾期内的，或焦点落回 ⋯ 触发器本身的，都不是用户想关面板。
   * 注意它只管 focus：真实的外部**点击**永远走上面那条，不会被这里吞掉。
   */
  const ignoreEchoFocus = (e: Event & { detail?: { originalEvent?: Event } }) => {
    const target = e.detail?.originalEvent?.target
    const backToTrigger = target instanceof Node && !!triggerRef.current?.contains(target)
    if (menuTeardownRef.current || backToTrigger) e.preventDefault()
  }

  const currentLocale = locale?.split('-')[0]

  return (
    <Popover open={downloadsOpen} onOpenChange={setDownloadsOpen}>
      <PopoverAnchor asChild>
        <div className="inline-flex">
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                ref={triggerRef}
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-accent"
                title={t('topbar.more')}
              >
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">{t('topbar.more')}</span>
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              align="end"
              className="w-72 bg-background border-border"
              onCloseAutoFocus={(e) => {
                if (skipMenuAutoFocusRef.current) {
                  e.preventDefault()
                  skipMenuAutoFocusRef.current = false
                }
              }}
            >
              {/* 动作项：icon + 标题 + 一行说明 */}
              <DropdownMenuItem
                className="cursor-pointer items-start gap-3 py-2.5"
                onSelect={() => openReportIssue({ userId, name: userName })}
              >
                <Bug className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <div className="text-sm text-foreground">{t('topbar.reportBug')}</div>
                  <div className="text-xs text-muted-foreground">{t('topbar.menu.reportBugDesc')}</div>
                </div>
              </DropdownMenuItem>

              <DropdownMenuItem
                className="cursor-pointer items-start gap-3 py-2.5"
                onSelect={openDownloads}
              >
                <Download className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <div className="text-sm text-foreground">{t('topbar.menu.downloads')}</div>
                  <div className="text-xs text-muted-foreground">{t('topbar.menu.downloadsDesc')}</div>
                </div>
              </DropdownMenuItem>

              <DropdownMenuSeparator className="bg-border" />

              {/* 语言：endonym 直接列出来（UI 文案规范的明确例外，不翻译语言名） */}
              <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
                {t('topbar.language')}
              </DropdownMenuLabel>
              {SUPPORTED_LOCALES.map((lng: AppLocale) => (
                <DropdownMenuItem
                  key={lng}
                  className="cursor-pointer justify-between gap-4 text-foreground"
                  onSelect={() => void setLocale(lng)}
                >
                  {LOCALE_ENDONYMS[lng]}
                  {currentLocale === lng && <Check className="h-4 w-4 text-primary" />}
                </DropdownMenuItem>
              ))}

              <DropdownMenuSeparator className="bg-border" />

              {/* Screen mode：三档分段控件，点了不关菜单（能立刻看见明暗变化） */}
              <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
                {t('topbar.menu.screenMode')}
              </DropdownMenuLabel>
              <div className="px-2 pb-1.5 pt-0.5">
                <div className="grid grid-cols-3 overflow-hidden rounded-md border border-border">
                  {THEMES.map(({ value, Icon }, i) => (
                    // 包一层 DropdownMenuItem(asChild) 只为拿到菜单的键盘可达性 ——
                    // 菜单里的裸 <button> 进不了 Radix 的 roving tabindex，键盘用户够不着。
                    // onSelect 里 preventDefault 把"选中即关闭"摘掉：切主题要当场看见效果。
                    // 切换只挂在 onSelect 上：Radix 的 menu item 对鼠标点击和键盘
                    // Enter/Space 都会派发 select，内层 button 再挂 onClick 就是同一次
                    // 操作触发两遍（幂等所以看不出来，但没有第二条路需要兜底）。
                    <DropdownMenuItem
                      key={value}
                      asChild
                      onSelect={(e) => {
                        e.preventDefault()
                        setTheme(value)
                      }}
                      className="p-0"
                    >
                      <button
                        type="button"
                        // 三选一的单选语义：DropdownMenuItem 默认派的是 menuitem，
                        // 这里显式覆盖成 menuitemradio + aria-checked（Slot 里子元素的 props 盖过父级）。
                        role="menuitemradio"
                        aria-checked={theme === value}
                        aria-label={tc(`ui.themeToggle.${value}`)}
                        title={tc(`ui.themeToggle.${value}`)}
                        className={cn(
                          'flex h-9 cursor-pointer items-center justify-center rounded-none transition-colors hover:bg-accent',
                          i > 0 && 'border-l border-border',
                          theme === value
                            ? 'bg-accent text-foreground'
                            : 'text-muted-foreground',
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </button>
                    </DropdownMenuItem>
                  ))}
                </div>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </PopoverAnchor>

      <PopoverContent
        className="w-96 p-0"
        align="end"
        sideOffset={8}
        onInteractOutside={ignoreEchoInteract}
        onFocusOutside={ignoreEchoFocus}
        // 面板挂的是 PopoverAnchor 不是 PopoverTrigger，Radix 没有天然的回焦目标，
        // 关掉后焦点会掉到 <body>，键盘用户丢一次位置。手动还给 ⋯ 按钮。
        onCloseAutoFocus={(e) => {
          e.preventDefault()
          triggerRef.current?.focus()
        }}
      >
        <ExportHistoryPanel open={downloadsOpen} />
      </PopoverContent>
    </Popover>
  )
}
